import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/about-teacher`;

const EMPTY_TEACHER = {
  teacherName: "",
  teacherImage: null,
  preview: "",
  role: "",
  description: "",
  certification: [""],
  experience: [""],
  expertise: [""],
};

const EMPTY_VIDEO = {
  video: null,
  thumbnail: null,
  preview: "",
  thumbnailPreview: "",
};

const emptyState = () => ({
  bannerData: {
    bannerImage: null,
    preview: "",
    mainSubHeading: "",
    heading: "",
  },
  teacherSection: {
    teacherHeading: "",
    teacherMainHeading: "",
    teacherMainPara: "",
    teachers: [{ ...EMPTY_TEACHER }],
  },
  videoSection: {
    videoHeading: "",
    videoMainHeading: "",
    videos: [{ ...EMPTY_VIDEO }],
  },
});

const resolveUploadUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${ENV_API_BASE_URL}${value}`;
  if (value.startsWith("uploads/")) return `${ENV_API_BASE_URL}/${value}`;
  return `${ENV_API_BASE_URL}/uploads/${value}`;
};

const normalizeArray = (value) =>
  Array.isArray(value) && value.length ? value : [""];

function AboutTeacherHeading() {
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [formState, setFormState] = useState(emptyState);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      const latest = Array.isArray(res.data?.data)
        ? res.data.data[0]
        : Array.isArray(res.data)
        ? res.data[0]
        : res.data?.data || res.data || null;

      if (!latest) {
        setRecordId(null);
        setFormState(emptyState());
        return;
      }

      setRecordId(latest._id || null);
      setFormState({
        bannerData: {
          bannerImage: latest.banner?.bannerImage || null,
          preview: resolveUploadUrl(latest.banner?.bannerImage),
          mainSubHeading: latest.banner?.mainSubHeading || "",
          heading: latest.banner?.heading || "",
        },
        teacherSection: {
          teacherHeading: latest.teacherSection?.teacherHeading || "",
          teacherMainHeading: latest.teacherSection?.teacherMainHeading || "",
          teacherMainPara: latest.teacherSection?.teacherMainPara || "",
          teachers:
            latest.teacherSection?.teachers?.length > 0
              ? latest.teacherSection.teachers.map((teacher) => ({
                  teacherName: teacher.teacherName || "",
                  teacherImage: teacher.teacherImage || null,
                  preview: resolveUploadUrl(teacher.teacherImage),
                  role: teacher.role || "",
                  description: teacher.description || "",
                  certification: normalizeArray(teacher.certification),
                  experience: normalizeArray(teacher.experience),
                  expertise: normalizeArray(teacher.expertise),
                }))
              : [{ ...EMPTY_TEACHER }],
        },
        videoSection: {
          videoHeading: latest.videoSection?.videoHeading || "",
          videoMainHeading: latest.videoSection?.videoMainHeading || "",
          videos:
            latest.videoSection?.videos?.length > 0
              ? latest.videoSection.videos.map((video) => ({
                  video: video.video || null,
                  thumbnail: video.thumbnail || null,
                  preview: resolveUploadUrl(video.video),
                  thumbnailPreview: resolveUploadUrl(video.thumbnail),
                }))
              : [{ ...EMPTY_VIDEO }],
        },
      });
    } catch (error) {
      console.error("Failed to load teachers admin data:", error);
      showToast("Failed to load existing teacher page data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { bannerData, teacherSection, videoSection } = formState;

  const setBannerData = (updater) =>
    setFormState((prev) => ({
      ...prev,
      bannerData: typeof updater === "function" ? updater(prev.bannerData) : updater,
    }));

  const setTeacherSection = (updater) =>
    setFormState((prev) => ({
      ...prev,
      teacherSection:
        typeof updater === "function" ? updater(prev.teacherSection) : updater,
    }));

  const setVideoSection = (updater) =>
    setFormState((prev) => ({
      ...prev,
      videoSection:
        typeof updater === "function" ? updater(prev.videoSection) : updater,
    }));

  const handleBannerChange = (e) => {
    const { name, value, files } = e.target;
    if (files?.[0]) {
      setBannerData((prev) => ({
        ...prev,
        bannerImage: files[0],
        preview: URL.createObjectURL(files[0]),
      }));
      return;
    }

    setBannerData((prev) => ({ ...prev, [name]: value }));
  };

  const updateTeacher = (index, field, value, file = null) => {
    const updated = [...teacherSection.teachers];
    if (file) {
      updated[index][field] = file;
      updated[index].preview = URL.createObjectURL(file);
    } else {
      updated[index][field] = value;
    }
    setTeacherSection((prev) => ({ ...prev, teachers: updated }));
  };

  const updateArrayField = (index, field, itemIndex, value) => {
    const updated = [...teacherSection.teachers];
    updated[index][field][itemIndex] = value;
    setTeacherSection((prev) => ({ ...prev, teachers: updated }));
  };

  const addArrayField = (index, field) => {
    const updated = [...teacherSection.teachers];
    updated[index][field].push("");
    setTeacherSection((prev) => ({ ...prev, teachers: updated }));
  };

  const removeArrayField = (index, field, itemIndex) => {
    const updated = [...teacherSection.teachers];
    updated[index][field] = updated[index][field].filter((_, i) => i !== itemIndex);
    if (!updated[index][field].length) updated[index][field] = [""];
    setTeacherSection((prev) => ({ ...prev, teachers: updated }));
  };

  const addTeacher = () => {
    setTeacherSection((prev) => ({
      ...prev,
      teachers: [...prev.teachers, { ...EMPTY_TEACHER }],
    }));
  };

  const removeTeacher = (index) => {
    setTeacherSection((prev) => ({
      ...prev,
      teachers:
        prev.teachers.length === 1
          ? [{ ...EMPTY_TEACHER }]
          : prev.teachers.filter((_, i) => i !== index),
    }));
  };

  const updateVideo = (index, field, file) => {
    const updated = [...videoSection.videos];
    updated[index][field] = file;
    if (field === "video") {
      updated[index].preview = URL.createObjectURL(file);
    }
    if (field === "thumbnail") {
      updated[index].thumbnailPreview = URL.createObjectURL(file);
    }
    setVideoSection((prev) => ({ ...prev, videos: updated }));
  };

  const updateVideoUrl = (index, value) => {
    const updated = [...videoSection.videos];
    updated[index].video = value;
    updated[index].preview = resolveUploadUrl(value);
    setVideoSection((prev) => ({ ...prev, videos: updated }));
  };

  const addVideo = () => {
    setVideoSection((prev) => ({
      ...prev,
      videos: [...prev.videos, { ...EMPTY_VIDEO }],
    }));
  };

  const removeVideo = (index) => {
    setVideoSection((prev) => ({
      ...prev,
      videos:
        prev.videos.length === 1
          ? [{ ...EMPTY_VIDEO }]
          : prev.videos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = new FormData();
    payload.append(
      "data",
      JSON.stringify({
        banner: {
          bannerImage:
            typeof bannerData.bannerImage === "string" ? bannerData.bannerImage : "",
          mainSubHeading: bannerData.mainSubHeading,
          heading: bannerData.heading,
        },
        teacherSection: {
          teacherHeading: teacherSection.teacherHeading,
          teacherMainHeading: teacherSection.teacherMainHeading,
          teacherMainPara: teacherSection.teacherMainPara,
          teachers: teacherSection.teachers.map((teacher) => ({
            teacherName: teacher.teacherName,
            teacherImage:
              typeof teacher.teacherImage === "string" ? teacher.teacherImage : "",
            role: teacher.role,
            description: teacher.description,
            certification: teacher.certification.filter(Boolean),
            experience: teacher.experience.filter(Boolean),
            expertise: teacher.expertise.filter(Boolean),
          })),
        },
        videoSection: {
          videoHeading: videoSection.videoHeading,
          videoMainHeading: videoSection.videoMainHeading,
          videos: videoSection.videos.map((video) => ({
            video: typeof video.video === "string" ? video.video : "",
            thumbnail: typeof video.thumbnail === "string" ? video.thumbnail : "",
          })),
        },
      })
    );

    if (bannerData.bannerImage instanceof File) {
      payload.append("bannerImage", bannerData.bannerImage);
    }

    teacherSection.teachers.forEach((teacher, index) => {
      if (teacher.teacherImage instanceof File) {
        payload.append(`teacherImage_${index}`, teacher.teacherImage);
      }
    });

    videoSection.videos.forEach((video, index) => {
      if (video.video instanceof File) payload.append(`video_${index}`, video.video);
      if (video.thumbnail instanceof File) {
        payload.append(`thumbnail_${index}`, video.thumbnail);
      }
    });

    try {
      if (recordId) {
        try {
          await axios.put(`${API}/${recordId}`, payload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (error) {
          // Some running backend instances may still expose only the legacy
          // create endpoint. That endpoint now behaves like an upsert for this
          // singleton page, so we can safely fall back to it.
          if (error?.response?.status !== 404) {
            throw error;
          }

          await axios.post(`${API}/create`, payload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      } else {
        await axios.post(`${API}/create`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      showToast("Teacher page saved successfully");
      loadData();
    } catch (error) {
      console.error("Failed to save teacher page:", error);
      showToast(
        error?.response?.data?.message || "Failed to save teacher page",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container pt-5 mt-5">Loading teacher page data...</div>;
  }

  return (
    <div className="container pt-5 mt-5 pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1">Teachers Page Admin</h3>
          <p className="text-muted mb-0">
            This page uses one shared record across the website and now loads the saved values automatically.
          </p>
        </div>
        <span className="badge text-bg-light border px-3 py-2">
          {recordId ? "Editing Live Record" : "New Shared Record"}
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-body">
            <h5>Banner</h5>
            <input
              type="text"
              name="mainSubHeading"
              placeholder="Sub Heading"
              className="form-control mb-2"
              value={bannerData.mainSubHeading}
              onChange={handleBannerChange}
            />
            <input
              type="text"
              name="heading"
              placeholder="Heading"
              className="form-control mb-2"
              value={bannerData.heading}
              onChange={handleBannerChange}
            />
            <input
              type="file"
              className="form-control mb-2"
              onChange={handleBannerChange}
            />
            {bannerData.preview && (
              <img src={bannerData.preview} width="150" alt="Banner preview" />
            )}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5>Teacher Section</h5>
            <input
              type="text"
              placeholder="Section Label"
              className="form-control mb-2"
              value={teacherSection.teacherHeading}
              onChange={(e) =>
                setTeacherSection((prev) => ({
                  ...prev,
                  teacherHeading: e.target.value,
                }))
              }
            />
            <input
              type="text"
              placeholder="Main Heading"
              className="form-control mb-2"
              value={teacherSection.teacherMainHeading}
              onChange={(e) =>
                setTeacherSection((prev) => ({
                  ...prev,
                  teacherMainHeading: e.target.value,
                }))
              }
            />
            <textarea
              placeholder="Main Paragraph"
              className="form-control mb-3"
              value={teacherSection.teacherMainPara}
              onChange={(e) =>
                setTeacherSection((prev) => ({
                  ...prev,
                  teacherMainPara: e.target.value,
                }))
              }
            />

            {teacherSection.teachers.map((teacher, index) => (
              <div key={index} className="border rounded p-3 mb-3">
                <div className="fw-semibold mb-2">Teacher {index + 1}</div>

                <input
                  type="text"
                  placeholder="Name"
                  className="form-control mb-2"
                  value={teacher.teacherName}
                  onChange={(e) =>
                    updateTeacher(index, "teacherName", e.target.value)
                  }
                />

                <input
                  type="file"
                  className="form-control mb-2"
                  onChange={(e) =>
                    updateTeacher(
                      index,
                      "teacherImage",
                      null,
                      e.target.files?.[0] || null
                    )
                  }
                />
                {teacher.preview && (
                  <img src={teacher.preview} width="120" alt="Teacher preview" />
                )}

                <input
                  type="text"
                  placeholder="Role"
                  className="form-control mb-2"
                  value={teacher.role}
                  onChange={(e) => updateTeacher(index, "role", e.target.value)}
                />

                <textarea
                  placeholder="Description"
                  className="form-control mb-2"
                  value={teacher.description}
                  onChange={(e) =>
                    updateTeacher(index, "description", e.target.value)
                  }
                />

                {["certification", "experience", "expertise"].map((field) => (
                  <div key={field} className="mb-2">
                    <div className="fw-semibold text-capitalize mb-2">{field}</div>
                    {teacher[field].map((item, itemIndex) => (
                      <div key={itemIndex} className="d-flex mb-2">
                        <input
                          className="form-control me-2"
                          value={item}
                          onChange={(e) =>
                            updateArrayField(index, field, itemIndex, e.target.value)
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => removeArrayField(index, field, itemIndex)}
                        >
                          X
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addArrayField(index, field)}
                      className="btn btn-sm btn-primary"
                    >
                      + Add {field}
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => removeTeacher(index)}
                  className="btn btn-danger mt-2"
                >
                  Remove Teacher
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addTeacher}
              className="btn btn-primary"
            >
              Add Teacher
            </button>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5>Videos</h5>
            <input
              type="text"
              placeholder="Video Section Label"
              className="form-control mb-2"
              value={videoSection.videoHeading}
              onChange={(e) =>
                setVideoSection((prev) => ({
                  ...prev,
                  videoHeading: e.target.value,
                }))
              }
            />
            <input
              type="text"
              placeholder="Video Main Heading"
              className="form-control mb-3"
              value={videoSection.videoMainHeading}
              onChange={(e) =>
                setVideoSection((prev) => ({
                  ...prev,
                  videoMainHeading: e.target.value,
                }))
              }
            />

            {videoSection.videos.map((video, index) => (
              <div key={index} className="border rounded p-3 mb-3">
                <div className="fw-semibold mb-2">Video {index + 1}</div>

                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Video URL (mp4/webm or direct file link)"
                  value={typeof video.video === "string" ? video.video : ""}
                  onChange={(e) => updateVideoUrl(index, e.target.value)}
                />

                <input
                  type="file"
                  className="form-control mb-2"
                  onChange={(e) => updateVideo(index, "video", e.target.files?.[0])}
                />
                <div className="form-text mb-2">
                  Use either a direct video URL or upload a file.
                </div>
                {video.preview && <video src={video.preview} width="220" controls />}

                <input
                  type="file"
                  className="form-control my-2"
                  onChange={(e) =>
                    updateVideo(index, "thumbnail", e.target.files?.[0])
                  }
                />
                {video.thumbnailPreview && (
                  <img
                    src={video.thumbnailPreview}
                    width="140"
                    alt="Thumbnail preview"
                  />
                )}

                <div>
                  <button
                    type="button"
                    onClick={() => removeVideo(index)}
                    className="btn btn-danger mt-2"
                  >
                    Remove Video
                  </button>
                </div>
              </div>
            ))}

            <button type="button" onClick={addVideo} className="btn btn-primary">
              Add Video
            </button>
          </div>
        </div>

        <button type="submit" className="btn btn-success" disabled={saving}>
          {saving ? "Saving..." : recordId ? "Update Teacher Page" : "Save Teacher Page"}
        </button>
      </form>

      {toast && (
        <div
          className={`toast show position-fixed bottom-0 end-0 m-4 align-items-center text-white border-0 ${
            toast.type === "error" ? "bg-danger" : "bg-success"
          }`}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{ zIndex: 1080 }}
        >
          <div className="d-flex">
            <div className="toast-body">{toast.msg}</div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              onClick={() => setToast(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AboutTeacherHeading;
